import { trackEvent as gtagTrackEvent } from "~/lib/gtag";
import { trackEvent as posthogTrackEvent } from "~/lib/posthog/analytics";
import type { UserRole } from "~/types";

/**
 * Track a sign up (account creation) event
 * @param userId - The user's ID (optional, may not exist yet)
 * @param userRole - The user's role (landlord or tenant)
 * @param accountType - The type of account (e.g., "beta-user", "standard")
 */
export const trackSignUp = (
  userId?: string,
  userRole?: UserRole,
  accountType?: string
): void => {
  gtagTrackEvent("sign_up", {
    user_id: userId,
    user_role: userRole,
    account_type: accountType || "standard",
    method: "email",
  });
  posthogTrackEvent("SIGN_UP", {
    user_id: userId,
    user_role: userRole,
    account_type: accountType || "standard",
    method: "email",
  });
};

/**
 * Track a login event
 * @param userId - The user's ID (optional)
 * @param userRole - The user's role (landlord or tenant)
 */
export const trackLogin = (userId?: string, userRole?: UserRole): void => {
  gtagTrackEvent("login", {
    user_id: userId,
    user_role: userRole,
    method: "email",
  });
  posthogTrackEvent("LOGIN", {
    user_id: userId,
    user_role: userRole,
    method: "email",
  });
};

/**
 * Track a form completion event
 * @param formTemplateSlug - The slug/ID of the form template
 * @param formName - The human-readable name of the form
 * @param userId - The user's ID (optional, may not be logged in)
 * @param submissionId - The ID of the form submission (optional)
 */
export const trackFormCompleted = (
  formTemplateSlug: string,
  formName: string,
  userId?: string,
  submissionId?: string
): void => {
  gtagTrackEvent("form_completed", {
    form_template: formTemplateSlug,
    form_name: formName,
    user_id: userId,
    submission_id: submissionId,
  });
  posthogTrackEvent("FORM_COMPLETED", {
    form_template: formTemplateSlug,
    form_name: formName,
    user_id: userId,
    submission_id: submissionId,
  });
};

/**
 * Track when a user starts filling out a form (first field change)
 * @param formTemplateSlug - The slug/ID of the form template
 * @param formName - The human-readable name of the form
 * @param stateSlug - The state slug if applicable (e.g., "california")
 * @param userId - The user's ID (optional, may not be logged in)
 */
export const trackFormStarted = (
  formTemplateSlug: string,
  formName: string,
  stateSlug?: string,
  userId?: string
): void => {
  posthogTrackEvent("FORM_STARTED", {
    form_template: formTemplateSlug,
    form_name: formName,
    form_state: stateSlug,
    user_id: userId,
  });
};

/**
 * Track a preview page event
 * @param userId - The user's ID (optional)
 * @param userRole - The user's role (landlord or tenant)
 */
export const trackPreviewVisit = (): void => {
  gtagTrackEvent("preview_visit");
  posthogTrackEvent("PREVIEW_VISIT");
};

/**
 * Track a begin_checkout event for Stripe subscriptions
 * @param priceId - The Stripe price ID being checked out
 */
export const trackBeginCheckout = (priceId: string): void => {
  gtagTrackEvent("begin_checkout", {
    price_id: priceId,
  });
  posthogTrackEvent("BEGIN_CHECKOUT", {
    price_id: priceId,
  });
};

/**
 * Parameters for tracking a trial started event.
 * These map to the subscription data available after Stripe checkout.
 */
interface TrialStartedParams {
  subscriptionId: string;
  customerId: string;
  priceId: string;
  priceName: string;
  priceAmount: number;
  productId: string;
  productName: string;
  trialEnd?: string | null;
  templateSlug?: string;
  stateSlug?: string;
}

/**
 * Track a trial_started event when a user begins a free trial.
 *
 * This event is fired after a user completes Stripe checkout and is redirected
 * back to StripeReturn.tsx with a valid trialing subscription.
 *
 * The event is sent to Google Analytics via gtag for conversion tracking.
 * Campaign parameters (gclid, utm_source, etc.) are automatically included
 * by the underlying trackEvent() function - see lib/gtag.ts.
 *
 * @param params - Subscription and form data to include in the event
 */
export const trackTrialStarted = ({
  subscriptionId,
  customerId,
  priceId,
  priceName,
  priceAmount,
  productId,
  productName,
  trialEnd,
  templateSlug,
  stateSlug,
}: TrialStartedParams): void => {
  gtagTrackEvent("trial_started", {
    subscription_id: subscriptionId,
    customer_id: customerId,
    price_id: priceId,
    price_name: priceName,
    value: priceAmount / 100,
    currency: "USD",
    product_id: productId,
    product_name: productName,
    trial_end: trialEnd,
    form_template: templateSlug,
    form_state: stateSlug,
  });
  posthogTrackEvent("TRIAL_STARTED", {
    subscription_id: subscriptionId,
    customer_id: customerId,
    price_id: priceId,
    price_name: priceName,
    value: priceAmount / 100,
    currency: "USD",
    product_id: productId,
    product_name: productName,
    trial_end: trialEnd,
    form_template: templateSlug,
    form_state: stateSlug,
  });
};
